import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TableListFooter from '../components/TableListFooter';
import DateRangeFilterModal from '../components/DateRangeFilterModal';
import OnServisDetailModal from '../components/OnServisDetailModal';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { parseApiDate } from '../utils/security';
import {
  DURUM_BADGE,
  DURUM_LABELS,
} from '../utils/onServisKaydiHelpers';
import {
  fetchOnServisKayitlari,
  fetchOnServisKaydiDetay,
  fetchOnServisIstatistik,
  updateOnServisKaydiDurum,
} from '../services/onServisKaydiService';
import './OnServisKayitlariPage.css';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const formatTarih = (val) => {
  const d = parseApiDate(val);
  if (!d || Number.isNaN(d.getTime())) return val || '—';
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getLastMonthDateRange = () => {
  const bitis = new Date();
  const baslangic = new Date();
  baslangic.setMonth(baslangic.getMonth() - 1);
  return {
    baslangicTarihi: formatDateInput(baslangic),
    bitisTarihi: formatDateInput(bitis),
  };
};

function DurumBadge({ durum }) {
  const style = DURUM_BADGE[durum] || { bg: '#f1f5f9', color: '#334155' };
  return (
    <span
      className="osk-badge"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {DURUM_LABELS[durum] || durum}
    </span>
  );
}

function OnServisKayitlariPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('kayitlar');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [durumFilter, setDurumFilter] = useState('');
  const [aramaInput, setAramaInput] = useState('');
  const [arama, setArama] = useState('');
  const [searchPending, setSearchPending] = useState(false);
  const [dateRange, setDateRange] = useState({});
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [sayfa, setSayfa] = useState(1);
  const [toplam, setToplam] = useState(0);
  const [toplamSayfa, setToplamSayfa] = useState(1);
  const [openDetailId, setOpenDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [savingDurum, setSavingDurum] = useState(false);
  const [stats, setStats] = useState({ bayiler: [], genelToplam: 0 });
  const [statsLoading, setStatsLoading] = useState(false);
  const debounceRef = useRef(null);
  const limit = PAGE_DEFAULT_LIMIT;

  const filterParams = useMemo(() => ({
    durum: durumFilter || undefined,
    arama: arama.trim() || undefined,
    baslangic: dateRange.baslangicTarihi,
    bitis: dateRange.bitisTarihi,
  }), [durumFilter, arama, dateRange.baslangicTarihi, dateRange.bitisTarihi]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchOnServisKayitlari({
        sayfa,
        limit,
        ...filterParams,
      });
      if (res?.basarili && res?.veri) {
        setList(res.veri.kayitlar || []);
        setToplam(res.veri.toplam ?? 0);
        setToplamSayfa(res.veri.toplamSayfa ?? 1);
      } else {
        setList([]);
        setToplam(0);
        setToplamSayfa(1);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Ön servis kayıtları yüklenemedi'));
      setList([]);
    } finally {
      setLoading(false);
      setSearchPending(false);
    }
  }, [sayfa, limit, filterParams]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await fetchOnServisIstatistik(filterParams);
      if (res?.basarili && res?.veri) {
        setStats({
          bayiler: res.veri.bayiler || [],
          genelToplam: res.veri.genelToplam ?? 0,
        });
      } else {
        setStats({ bayiler: [], genelToplam: 0 });
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'İstatistik yüklenemedi'));
      setStats({ bayiler: [], genelToplam: 0 });
    } finally {
      setStatsLoading(false);
      setSearchPending(false);
    }
  }, [filterParams]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (aramaInput === arama) {
      setSearchPending(false);
      return undefined;
    }
    setSearchPending(true);
    debounceRef.current = setTimeout(() => {
      setArama(aramaInput);
      setSayfa(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [aramaInput, arama]);

  useEffect(() => {
    if (activeTab === 'istatistik' && !dateRange.baslangicTarihi && !dateRange.bitisTarihi) {
      setDateRange(getLastMonthDateRange());
    }
  }, [activeTab, dateRange.baslangicTarihi, dateRange.bitisTarihi]);

  useEffect(() => {
    if (activeTab === 'kayitlar') loadList();
  }, [activeTab, loadList]);

  useEffect(() => {
    if (activeTab === 'istatistik') loadStats();
  }, [activeTab, loadStats]);

  const loadDetail = async (id) => {
    setOpenDetailId(id);
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await fetchOnServisKaydiDetay(id);
      setDetail(res?.veri?.onServisKaydi || null);
    } catch (err) {
      setDetail(null);
      setDetailError(getErrorMessage(err, 'Detay yüklenemedi'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDurumChange = async (id, durum) => {
    setSavingDurum(true);
    try {
      await updateOnServisKaydiDurum(id, durum);
      if (activeTab === 'kayitlar') await loadList();
      if (openDetailId === id) await loadDetail(id);
      window.showNotification?.('success', 'Durum güncellendi');
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Durum güncellenemedi'));
    } finally {
      setSavingDurum(false);
    }
  };

  const goToServisKaydi = (item) => {
    if (!item?.id) return;
    setOpenDetailId(null);
    if (item.servisKaydiId) {
      navigate(`/service-records/${item.servisKaydiId}/update`);
      return;
    }
    navigate(`/service-records/create?onServisKaydiId=${item.id}`);
  };

  const dateFilterLabel = dateRange.baslangicTarihi || dateRange.bitisTarihi
    ? `${dateRange.baslangicTarihi || '…'} – ${dateRange.bitisTarihi || '…'}`
    : 'Tarih aralığı';

  const showSearchSpinner = searchPending || (loading && activeTab === 'kayitlar') || (statsLoading && activeTab === 'istatistik');

  const renderTable = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner" />
          <div className="loading-text">Kayıtlar yükleniyor</div>
          <div className="loading-dots">
            <span />
            <span />
            <span />
          </div>
        </div>
      );
    }

    if (list.length === 0) {
      return (
        <div className="empty-state">
          <i className="fas fa-inbox" />
          <span>Kayıt bulunamadı</span>
        </div>
      );
    }

    return (
      <table className="admins-table">
        <thead>
          <tr>
            <th>Tarih</th>
            <th>Müşteri</th>
            <th>Ürün / Seri</th>
            <th>Bayi</th>
            <th>Durum</th>
            <th className="islemler-sutunu">İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {list.map((item) => {
            const musteriAd = `${item.musteriAd || ''} ${item.musteriSoyad || ''}`.trim() || '—';
            return (
              <tr
                key={item.id}
                onClick={() => loadDetail(item.id)}
                style={{ cursor: 'pointer' }}
              >
                <td>{formatTarih(item.createdAt)}</td>
                <td className="osk-customer-cell">
                  <strong>{musteriAd}</strong>
                  <small>{item.telefon || '—'}</small>
                </td>
                <td className="osk-product-cell">
                  <strong title={item.urunAdi}>{item.urunAdi || '—'}</strong>
                  {item.seriNumarasi ? (
                    <small>S/N: {item.seriNumarasi}</small>
                  ) : (
                    <small className="osk-muted">Seri no yok</small>
                  )}
                </td>
                <td className="osk-cell-ellipsis" title={item.bayiAdi}>{item.bayiAdi || '—'}</td>
                <td><DurumBadge durum={item.durum} /></td>
                <td className="islemler-sutunu" onClick={(e) => e.stopPropagation()}>
                  <div className="table-actions">
                    <button
                      type="button"
                      className="category-action-btn edit-btn"
                      onClick={() => loadDetail(item.id)}
                      title="Detayları görüntüle"
                    >
                      <i className="fas fa-info-circle" />
                    </button>
                    <button
                      type="button"
                      className="category-action-btn hakedis-btn"
                      onClick={() => goToServisKaydi(item)}
                      title={item.servisKaydiId ? 'Servis kaydına git' : 'Servis kaydı oluştur'}
                    >
                      <i className={`fas ${item.servisKaydiId ? 'fa-external-link-alt' : 'fa-plus'}`} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderStatsTable = () => {
    if (statsLoading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner" />
          <div className="loading-text">İstatistikler yükleniyor</div>
        </div>
      );
    }

    if (stats.bayiler.length === 0) {
      return (
        <div className="empty-state">
          <i className="fas fa-chart-bar" />
          <span>Seçilen aralıkta kayıt yok</span>
        </div>
      );
    }

    const maxToplam = stats.bayiler[0]?.toplam || 1;

    return (
      <table className="admins-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Bayi</th>
            <th>Adet</th>
            <th>Oran</th>
          </tr>
        </thead>
        <tbody>
          {stats.bayiler.map((row, idx) => {
            const pct = stats.genelToplam > 0
              ? Math.round((row.toplam / stats.genelToplam) * 100)
              : 0;
            const barWidth = maxToplam > 0 ? Math.round((row.toplam / maxToplam) * 100) : 0;
            return (
              <tr key={row.bayiKullaniciId || idx}>
                <td>{idx + 1}</td>
                <td>{row.bayiAdi}</td>
                <td><strong>{row.toplam}</strong></td>
                <td className="osk-bar-cell">
                  <div className="osk-bar-track">
                    <div className="osk-bar-fill" style={{ width: `${barWidth}%` }} />
                  </div>
                  <span className="osk-bar-label">%{pct}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <main className="main-content service-records on-servis-kayitlari-page">
      <div className="content-header">
        <h1>Ön Servis Kayıtları</h1>
      </div>

      <div className="osk-tabs">
        <button
          type="button"
          className={`status-tab-btn ${activeTab === 'kayitlar' ? 'active' : ''}`}
          onClick={() => setActiveTab('kayitlar')}
        >
          Kayıtlar
          {toplam > 0 && activeTab === 'kayitlar' ? (
            <span className="osk-tab-badge">{toplam}</span>
          ) : null}
        </button>
        <button
          type="button"
          className={`status-tab-btn ${activeTab === 'istatistik' ? 'active' : ''}`}
          onClick={() => setActiveTab('istatistik')}
        >
          İstatistik
        </button>
      </div>

      <div className="toolbar">
        <div className="search-filter-bar" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-box" style={{ flex: 1, minWidth: '250px' }}>
            {showSearchSpinner ? (
              <span style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'none',
              }}
              >
                <i className="fas fa-spinner fa-spin" style={{ color: 'var(--text-gray)' }} />
              </span>
            ) : (
              <i className="fas fa-search" />
            )}
            <input
              type="search"
              placeholder="Ad, telefon, bayi, ürün veya servis no"
              value={aramaInput}
              onChange={(e) => setAramaInput(e.target.value)}
              maxLength={100}
              autoComplete="off"
            />
          </div>

          <select
            value={durumFilter}
            onChange={(e) => { setDurumFilter(e.target.value); setSayfa(1); }}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#ffffff',
              minWidth: '150px',
            }}
          >
            <option value="">Tüm Durumlar</option>
            {Object.entries(DURUM_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDateModalOpen(true)}
          >
            <i className="fas fa-calendar-alt" style={{ marginRight: 6 }} />
            {dateFilterLabel}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {activeTab === 'kayitlar' && (
        <div className="table-container" style={{ background: 'transparent', border: 'none' }}>
          <div className="osk-table-card">
            {renderTable()}
            {!loading && list.length > 0 && (
              <TableListFooter
                totalRecords={toplam}
                currentPage={sayfa}
                totalPages={toplamSayfa}
                onPageChange={setSayfa}
                loading={loading}
                embedded
              />
            )}
          </div>
        </div>
      )}

      {activeTab === 'istatistik' && (
        <div className="osk-stats-panel">
          <div className="osk-stats-summary">
            <div className="osk-stat-card">
              <span>Toplam ön kayıt</span>
              <strong>{stats.genelToplam}</strong>
            </div>
            <div className="osk-stat-card">
              <span>Bayi sayısı</span>
              <strong>{stats.bayiler.length}</strong>
            </div>
          </div>
          <div className="osk-table-card">
            {renderStatsTable()}
          </div>
        </div>
      )}

      <DateRangeFilterModal
        isOpen={dateModalOpen}
        onClose={() => setDateModalOpen(false)}
        initialRange={dateRange}
        onApply={(range) => {
          setDateRange(range);
          setSayfa(1);
        }}
      />

      {openDetailId ? (
        <OnServisDetailModal
          detail={detail}
          loading={detailLoading}
          error={detailError}
          onClose={() => {
            setOpenDetailId(null);
            setDetail(null);
            setDetailError('');
          }}
          onDurumChange={handleDurumChange}
          onGoToServisKaydi={goToServisKaydi}
          savingDurum={savingDurum}
        />
      ) : null}
    </main>
  );
}

export default OnServisKayitlariPage;
